/**
 * Admin Users Hooks
 *
 * RPCs used:
 * - admin_list_users_paginated(p_page, p_page_size, p_search, p_sort_by, p_sort_asc)
 * - admin_get_user_stats()
 * - admin_search_users(p_search_query, p_search_by_email, p_result_limit)
 * - admin_get_user_profile(p_target_user_id)
 * - admin_get_user_profile_details(p_user_id)
 * - admin_get_user_activity_timeseries(p_user_id, p_days)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AdminUserProfile {
    user_id: string;
    display_name: string | null;
    email: string | null;
    banned_at?: string | null;
    is_admin?: boolean;
    avatar_url: string | null;
    country_code: string | null;
    country_name?: string | null;
    region?: string | null;
    platform?: string | null;
    subscription_tier?: string | null;
    captures_count?: number;
    last_seen?: string;
    created_at: string;
    /** Onboarding tipo V2 persistido en servidor (bloqueo de flujos como añadir captura si es false). */
    onboarding_completed?: boolean | null;
    /** Checklist «Completa tu perfil» (pasos confirmados en servidor). */
    profile_onboarding_ack?: Record<string, unknown> | null;
}

export interface AdminUserStats {
    total_users: number;
    users_with_display_name: number;
}

export type SortField = 'display_name' | 'email' | 'created_at' | 'captures_count' | 'subscription_tier' | 'last_seen';

export interface PaginatedUsersResult {
    users: AdminUserProfile[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
}

export interface UserAppVersionInfo {
    app_version: string;
    platform?: string | null;
    device_model?: string | null;
    os_version?: string | null;
    last_seen?: string | null;
    first_seen?: string | null;
}

export interface RecommendedAppVersion {
    version: string;
    version_code?: number;
    build_timestamp?: number;
}

export interface UserDetailData {
    profile: AdminUserProfile;
    subscription: {
        status: string;
        plan_id: string;
        stripe_customer_id: string | null;
        stripe_subscription_id: string | null;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
    } | null;
    gamification: {
        current_level: number;
        current_energy: number;
        max_energy: number;
        victories: number;
        defeats: number;
        avatar_type: string;
        avatar_stats: {
            power: number;
            speed: number;
            defense: number;
        };
    } | null;
    token_balance: {
        current_tokens: number;
        tokens_used_this_period: number;
        tokens_granted_this_period: number;
    } | null;
    stats: {
        total_captures: number;
        unique_species: number;
        total_battles: number;
        battles_activator: number;
        battles_won: number;
        total_quizzes: number;
        avg_quiz_score: number;
        total_followers: number;
        total_following: number;
        ai_videos_generated: number;
        last_active_at: string | null;
    };
    recent_activity: {
        event_type: string;
        created_at: string;
        metadata: any;
    }[];
    /** Última versión reportada por la app (user_app_versions), o null si no hay telemetría. */
    app_version?: UserAppVersionInfo | null;
    /** Versión recomendada configurada en Admin → Versiones (app_config). */
    recommended_version?: RecommendedAppVersion | null;
}

export interface UserActivityTimeseries {
    date: string;
    captures_count: number;
    battles_count: number;
    quizzes_count: number;
    sessions_count: number;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useAdminUserStats() {
    return useQuery<AdminUserStats>({
        queryKey: ['admin', 'user-stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_user_stats');
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] || { total_users: 0, users_with_display_name: 0 }) as AdminUserStats;
        },
        staleTime: 60_000,
    });
}

export function useAdminListUsers(options: {
    page: number;
    pageSize: number;
    search: string;
    sortBy: SortField;
    sortAsc: boolean;
}) {
    const { page, pageSize, search, sortBy, sortAsc } = options;

    return useQuery<PaginatedUsersResult>({
        queryKey: ['admin', 'list-users', page, pageSize, search, sortBy, sortAsc],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_users_paginated', {
                p_page: page,
                p_page_size: pageSize,
                p_search: search.trim(),
                p_sort_by: sortBy,
                p_sort_asc: sortAsc,
            });
            if (error) throw error;

            const normalized = normalizeRpcResponse(data) as any[];
            const totalCount = normalized[0]?.total_count ?? 0;

            return {
                users: normalized,
                totalCount,
                totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
                currentPage: page,
            };
        },
        staleTime: 30_000,
        placeholderData: (prev) => prev, // Keep previous data while loading new page
    });
}

export function useAdminSearchUsers(searchQuery: string, options?: {
    searchByEmail?: boolean;
    limit?: number;
}) {
    const { searchByEmail = true, limit = 20 } = options || {};

    return useQuery<AdminUserProfile[]>({
        queryKey: ['admin', 'search-users', searchQuery, searchByEmail, limit],
        queryFn: async () => {
            if (!searchQuery || searchQuery.trim().length < 1) return [];

            const { data, error } = await dbClient.rpc('admin_search_users', {
                p_search_query: searchQuery.trim(),
                p_search_by_email: searchByEmail,
                p_result_limit: limit,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as AdminUserProfile[];
        },
        enabled: searchQuery.trim().length >= 1,
        staleTime: 30_000,
    });
}

export function useAdminUserProfile(userId: string | null) {
    return useQuery<AdminUserProfile | null>({
        queryKey: ['admin', 'user-profile', userId],
        queryFn: async () => {
            if (!userId) return null;
            const { data, error } = await dbClient.rpc('admin_get_user_profile', {
                p_target_user_id: userId,
            });
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] ?? null) as AdminUserProfile | null;
        },
        enabled: !!userId,
        staleTime: 60_000,
    });
}

export function useAdminUserProfileDetails(userId: string | null) {
    return useQuery<UserDetailData | null>({
        queryKey: ['admin', 'user-profile-details', userId],
        queryFn: async () => {
            if (!userId) return null;
            const { data, error } = await dbClient.rpc('admin_get_user_profile_details', {
                p_user_id: userId,
            });
            if (error) throw error;

            // Safe handling: normalizeRpcResponse ensures we get an array, then take the first item
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] ?? null) as UserDetailData | null;
        },
        enabled: !!userId,
        staleTime: 60_000,
    });
}

export function useAdminUserActivityTimeseries(userId: string | null, days: number = 30) {
    return useQuery<UserActivityTimeseries[]>({
        queryKey: ['admin', 'user-activity-timeseries', userId, days],
        queryFn: async () => {
            if (!userId) return [];
            const { data, error } = await dbClient.rpc('admin_get_user_activity_timeseries', {
                p_user_id: userId,
                p_days: days,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as UserActivityTimeseries[];
        },
        enabled: !!userId,
        staleTime: 300_000, // 5 minutes
    });
}

export interface AdminUpdateUserProfileParams {
    display_name?: string | null;
    avatar_url?: string | null;
    country_code?: string | null;
    country_name?: string | null;
    region?: string | null;
    /** Si se omite, la RPC no modifica onboarding_completed */
    onboarding_completed?: boolean | null;
    /** Si se omite, la RPC no modifica profile_onboarding_ack */
    profile_onboarding_ack?: Record<string, unknown> | null;
}

export function useAdminUpdateUserProfile(userId: string | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: AdminUpdateUserProfileParams) => {
            if (!userId) throw new Error('User ID required');
            const rpcArgs: Record<string, unknown> = {
                p_user_id: userId,
                p_display_name: params.display_name ?? undefined,
                p_avatar_url: params.avatar_url ?? undefined,
                p_country_code: params.country_code ?? undefined,
                p_country_name: params.country_name ?? undefined,
                p_region: params.region ?? undefined,
            };
            if (typeof params.onboarding_completed === 'boolean') {
                rpcArgs.p_onboarding_completed = params.onboarding_completed;
            }
            if (params.profile_onboarding_ack !== undefined) {
                rpcArgs.p_profile_onboarding_ack = params.profile_onboarding_ack;
            }
            const { error } = await dbClient.rpc('admin_update_user_profile', rpcArgs);
            if (error) throw error;
        },
        onSuccess: () => {
            if (userId) {
                queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile', userId] });
                queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
                queryClient.invalidateQueries({ queryKey: ['admin', 'list-users'] });
            }
        },
    });
}
